import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime, fmtDuration, fmtNum } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { MeResponse, Snapshot } from '@sklad-sync/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Props = {
  user: MeResponse;
};

export function DashboardPage({ user }: Props) {
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' | 'info' } | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ snapshots: Snapshot[] }>('/api/snapshots');
      setSnapshots(data.snapshots);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function manualRun() {
    if (!confirm('Spustit manuální sync? Může to trvat několik minut.')) return;
    setRunning(true);
    try {
      const r = await apiFetch<{ status: string; mismatch_count: number }>('/api/snapshots/run', {
        method: 'POST',
      });
      setToast({
        msg: `Sync hotov: ${r.status}, ${r.mismatch_count} nesrovnalostí`,
        kind: r.status === 'success' ? 'success' : 'info',
      });
      await load();
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.message : 'Sync selhal',
        kind: 'error',
      });
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Dashboard</h1>
          <p className="text-sm text-text-secondary mt-0.5">Posledních 30 denních běhů</p>
        </div>
        {hasModule(user.modules, 'sklad_sync') && (
          <button
            type="button"
            onClick={manualRun}
            disabled={running}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
          >
            {running ? 'Probíhá…' : 'Spustit sync'}
          </button>
        )}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg">
              <Th>Datum a čas</Th>
              <Th>Stav</Th>
              <Th>Varianty</Th>
              <Th>Nesrovnalosti</Th>
              <Th>Zdroje</Th>
              <Th>Trvání</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody>
            {snapshots == null ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : snapshots.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-text-muted">
                  Zatím neproběhl žádný sync.
                </td>
              </tr>
            ) : (
              snapshots.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-border-light hover:bg-surface-hover transition-colors"
                >
                  <td className="px-5 py-3 text-text">{fmtDateTime(s.run_at)}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-5 py-3 text-text">{fmtNum(s.variants_total)}</td>
                  <td className="px-5 py-3">
                    {s.mismatch_count === 0 ? (
                      <span className="text-success font-medium">0</span>
                    ) : (
                      <span className="text-danger font-semibold">{fmtNum(s.mismatch_count)}</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <SourceFlags
                      shoptet={!!s.shoptet_ok}
                      brani={!!s.brani_ok}
                      vyroba={!!s.vyroba_ok}
                    />
                  </td>
                  <td className="px-5 py-3 text-text-secondary">{fmtDuration(s.duration_ms)}</td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/sklad-sync/snapshot/${s.id}`}
                      className="text-sm font-semibold text-primary hover:text-primary-dark"
                    >
                      Detail →
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider px-5 py-3">
      {children}
    </th>
  );
}

function StatusBadge({ status }: { status: 'success' | 'partial' | 'failed' }) {
  const map = {
    success: { cls: 'text-success bg-success-light', label: 'OK' },
    partial: { cls: 'text-warning bg-warning-light', label: 'Částečně' },
    failed: { cls: 'text-danger bg-danger-light', label: 'Selhalo' },
  } as const;
  const { cls, label } = map[status];
  return <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}

function SourceFlags({
  shoptet,
  brani,
  vyroba,
}: {
  shoptet: boolean;
  brani: boolean;
  vyroba: boolean;
}) {
  return (
    <div className="flex gap-1.5">
      <Pill ok={shoptet} label="S" title="Shoptet" />
      <Pill ok={brani} label="B" title="Sklad ERP + SK nároky" />
      <Pill ok={vyroba} label="V" title="Výroba" />
    </div>
  );
}

function Pill({ ok, label, title }: { ok: boolean; label: string; title: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded ${
        ok ? 'bg-success-light text-success' : 'bg-danger-light text-danger'
      }`}
    >
      {label}
    </span>
  );
}
